import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import api from "@/lib/api";

const STORAGE_KEY = "aicom_cookie_consent_v1";

// GA4 client_id from the _ga cookie (inline to avoid a circular import with Analytics).
const readGaClientId = () => {
  try {
    const raw = document.cookie.split("; ").find((c) => c.startsWith("_ga="))?.split("=")[1];
    if (!raw) return null;
    const p = decodeURIComponent(raw).split(".");
    return p.length >= 4 ? `${p[p.length - 2]}.${p[p.length - 1]}` : null;
  } catch { return null; }
};

const APP_PREFIXES = [
  "/onboarding", "/betaling", "/dashboard", "/ugens-forslag", "/idebank",
  "/brandfortaelling", "/linkedin", "/facebook", "/nyhedsbrev", "/presse",
  "/kampagner", "/leadgenerator", "/projekter", "/vidensbank", "/indstillinger",
  "/priser", "/admin",
];

export function getCookieConsent() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY)); } catch { return null; }
}

export default function CookieConsent() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [statistics, setStatistics] = useState(true);
  const [marketing, setMarketing] = useState(true);
  const [hasConsent, setHasConsent] = useState(false);

  const isAppRoute = APP_PREFIXES.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"));

  useEffect(() => {
    const c = getCookieConsent();
    if (c) {
      setHasConsent(true);
      setStatistics(!!c.statistics);
      setMarketing(!!c.marketing);
    } else {
      const t = setTimeout(() => setOpen(true), 600);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    const reopen = () => {
      const c = getCookieConsent();
      if (c) { setStatistics(!!c.statistics); setMarketing(!!c.marketing); }
      setShowSettings(true);
      setOpen(true);
    };
    window.addEventListener("open-cookie-settings", reopen);
    return () => window.removeEventListener("open-cookie-settings", reopen);
  }, []);

  const save = (choice) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...choice, necessary: true, ts: new Date().toISOString() }));
    window.dispatchEvent(new CustomEvent("cookie-consent-updated", { detail: choice }));
    setHasConsent(true);
    setOpen(false);
    // Persist the LATEST consent server-side for logged-in users (accept OR revoke) so
    // future Stripe->GA4 conversions respect the newest choice, not a stale checkout snapshot.
    if (localStorage.getItem("pt_token")) {
      api.post("/consent", {
        statistics: !!choice.statistics,
        marketing: !!choice.marketing,
        ga_client_id: choice.statistics ? readGaClientId() : null,
        version: "v1",
      }).catch(() => {});
    }
  };

  if (isAppRoute) return null;

  return (
    <>
      <AnimatePresence>
        {!open && hasConsent && (
          <motion.button
            initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.6 }}
            transition={{ duration: 0.25 }}
            onClick={() => { setShowSettings(true); setOpen(true); }}
            aria-label="Cookieindstillinger"
            className={`fixed bottom-4 left-4 z-[90] h-11 w-11 rounded-full border border-border bg-card/95 backdrop-blur-xl shadow-xl shadow-black/40 ${location.pathname === "/kom-i-gang" ? "hidden md:flex" : "flex"} items-center justify-center text-foreground hover:bg-secondary transition-colors`}
            data-testid="cookie-reopen-btn">
            <Cookie className="h-5 w-5" strokeWidth={1.75} />
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {open && (
        <motion.div
          initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 40 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          className="fixed bottom-0 left-0 right-0 sm:right-auto sm:left-6 sm:bottom-6 z-[100] sm:max-w-md pb-[env(safe-area-inset-bottom)] sm:pb-0"
          data-testid="cookie-consent">
          <div className="rounded-t-2xl sm:rounded-2xl border border-border bg-card/95 backdrop-blur-xl shadow-2xl shadow-black/20 p-5 sm:p-6">
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-xl bg-secondary flex items-center justify-center shrink-0">
                <Cookie className="h-5 w-5 text-foreground" strokeWidth={1.75} />
              </div>
              <div>
                <h3 className="font-display text-base font-medium text-foreground">Vi bruger cookies</h3>
                <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">
                  Vi bruger cookies til at få hjemmesiden til at fungere samt til statistik og markedsføring. Du bestemmer selv, hvad du samtykker til. Læs mere i vores{" "}
                  <Link to="/privatlivspolitik" className="text-foreground underline underline-offset-2 hover:no-underline" data-testid="cookie-privacy-link">privatlivspolitik</Link>.
                </p>
              </div>
            </div>

            <AnimatePresence initial={false}>
              {showSettings && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden">
                  <div className="mt-4 space-y-3 border-t border-border pt-4">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <p className="text-sm font-medium text-foreground">Nødvendige</p>
                        <p className="text-xs text-muted-foreground">Kræves for at siden fungerer.</p>
                      </div>
                      <Switch checked disabled data-testid="cookie-necessary-switch" />
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <p className="text-sm font-medium text-foreground">Statistik</p>
                        <p className="text-xs text-muted-foreground">Hjælper os med at forbedre siden.</p>
                      </div>
                      <Switch checked={statistics} onCheckedChange={setStatistics} data-testid="cookie-statistics-switch" />
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <p className="text-sm font-medium text-foreground">Marketing</p>
                        <p className="text-xs text-muted-foreground">Bruges til relevant annoncering.</p>
                      </div>
                      <Switch checked={marketing} onCheckedChange={setMarketing} data-testid="cookie-marketing-switch" />
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="mt-5 flex flex-col sm:flex-row gap-2">
              {showSettings ? (
                <button onClick={() => save({ statistics, marketing })}
                  className="sm:flex-1 h-11 sm:h-10 shrink-0 rounded-full bg-foreground text-background text-sm font-medium hover:opacity-90 transition-opacity" data-testid="cookie-save-btn">
                  Gem valg
                </button>
              ) : (
                <button onClick={() => save({ statistics: true, marketing: true })}
                  className="sm:flex-1 h-11 sm:h-10 shrink-0 rounded-full bg-foreground text-background text-sm font-medium hover:opacity-90 transition-opacity" data-testid="cookie-accept-all-btn">
                  Accepter alle
                </button>
              )}
              <button onClick={() => save({ statistics: false, marketing: false })}
                className="sm:flex-1 h-11 sm:h-10 shrink-0 rounded-full border border-border text-foreground text-sm font-medium hover:bg-secondary transition-colors" data-testid="cookie-necessary-only-btn">
                Kun nødvendige
              </button>
              <button onClick={() => setShowSettings((s) => !s)}
                className="h-11 sm:h-10 shrink-0 px-4 rounded-full text-muted-foreground text-sm font-medium hover:text-foreground transition-colors" data-testid="cookie-settings-btn">
                {showSettings ? "Skjul" : "Tilpas"}
              </button>
            </div>
          </div>
        </motion.div>
      )}
      </AnimatePresence>
    </>
  );
}
